import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import {
  ChevronLeft,
  ChevronRight,
  User,
  Bell,
  Lock,
  Palette,
  LogOut,
  Trash2,
  Shield,
  Eye,
  EyeOff,
  Loader2,
  Check,
  Moon,
  Sun,
  AlertTriangle,
  BookOpen,
} from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { useUpdateProfile } from '@/hooks/useUser';
import { getSdk } from '@/lib/sdk';
import { processAvatarUrl } from '@/lib/mediaUtils';

/* ─── Types ─── */

type Section = 'main' | 'account' | 'notifications' | 'privacy' | 'appearance';

/* ─── Toggle row ─── */

function ToggleRow({ label, description, value, onChange }: {
  label: string;
  description?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between py-3.5">
      <div className="flex-1 min-w-0 pr-4">
        <p className="text-sm text-[#F5F7FF]">{label}</p>
        {description && <p className="text-xs text-[#A7B0C8] mt-0.5">{description}</p>}
      </div>
      <button
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${value ? 'bg-[#A38BFF]' : 'bg-[#2A2F3A]'}`}
      >
        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${value ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
}

/* ─── Nav row ─── */

function NavRow({ icon, label, description, onClick, danger }: {
  icon: React.ReactNode;
  label: string;
  description?: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 py-3.5 text-left active:bg-white/[0.02] transition-colors"
    >
      <span className={`flex-shrink-0 ${danger ? 'text-[#FF4D4D]' : 'text-[#A7B0C8]'}`}>{icon}</span>
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${danger ? 'text-[#FF4D4D]' : 'text-[#F5F7FF]'}`}>{label}</p>
        {description && <p className="text-xs text-[#A7B0C8] mt-0.5">{description}</p>}
      </div>
      {!danger && <ChevronRight size={16} className="text-[#A7B0C8] flex-shrink-0" />}
    </button>
  );
}

/* ─── Section card ─── */

function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-[#141821] rounded-2xl px-4 divide-y divide-white/[0.05]">
      {children}
    </div>
  );
}

/* ─── Main component ─── */

interface SettingsProps {
  onBack?: () => void;
}

export default function Settings({ onBack }: SettingsProps) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const userRecord = user as Record<string, unknown> | null;
  const updateMutation = useUpdateProfile();

  const [section, setSection] = useState<Section>('main');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  // Account fields
  const [displayName, setDisplayName] = useState((userRecord?.displayName as string) || (userRecord?.name as string) || '');
  const [email] = useState((userRecord?.email as string) || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);

  // Notification toggles
  const [notifLikes, setNotifLikes] = useState(true);
  const [notifComments, setNotifComments] = useState(true);
  const [notifFollows, setNotifFollows] = useState(true);
  const [notifMentions, setNotifMentions] = useState(true);
  const [notifMessages, setNotifMessages] = useState(true);

  // Privacy toggles
  const [privateAccount, setPrivateAccount] = useState(false);
  const [showActivity, setShowActivity] = useState(true);
  const [allowMentions, setAllowMentions] = useState(true);

  // Appearance
  const [theme] = useState<'dark'>('dark'); // dark-only for now

  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    gsap.fromTo(headerRef.current, { y: -20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.35, ease: 'power2.out' });
    gsap.fromTo(contentRef.current, { opacity: 0, y: 8 }, { opacity: 1, y: 0, duration: 0.35, delay: 0.05, ease: 'power2.out' });
  }, [section]);

  const handleBack = () => {
    if (section !== 'main') { setSection('main'); return; }
    if (onBack) onBack(); else navigate(-1);
  };

  const handleSaveAccount = async () => {
    if (!displayName.trim()) return;
    setIsSaving(true);
    try {
      await updateMutation.mutateAsync({ displayName: displayName.trim() });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    if (newPassword !== confirmPassword) { setPasswordError('Passwords do not match'); return; }
    if (newPassword.length < 8) { setPasswordError('Password must be at least 8 characters'); return; }
    try {
      const sdk = getSdk();
      await sdk.changePassword(currentPassword, newPassword, confirmPassword);
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      setPasswordSaved(true);
      setTimeout(() => setPasswordSaved(false), 2000);
    } catch {
      setPasswordError('Current password is incorrect');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true);
    try {
      const sdk = getSdk();
      await sdk.deleteAccount();
      await logout();
      navigate('/login', { replace: true });
    } catch {
      setIsDeletingAccount(false);
      setShowDeleteConfirm(false);
    }
  };

  const sectionTitle: Record<Section, string> = {
    main: 'Settings',
    account: 'Account',
    notifications: 'Notifications',
    privacy: 'Privacy',
    appearance: 'Appearance',
  };

  return (
    <div className="min-h-screen bg-[#0B0D10] pb-20">
      {/* Header */}
      <div
        ref={headerRef}
        className="sticky top-0 z-40 bg-[#0B0D10]/95 backdrop-blur-xl border-b border-white/[0.06] px-4 py-3 pt-safe flex items-center gap-3"
      >
        <button
          aria-label="Back"
          onClick={handleBack}
          className="w-10 h-10 rounded-full bg-[#141821] flex items-center justify-center text-[#A7B0C8] flex-shrink-0"
        >
          <ChevronLeft size={22} />
        </button>
        <h1 className="text-base font-semibold text-[#F5F7FF] flex-1" style={{ fontFamily: 'Space Grotesk' }}>
          {sectionTitle[section]}
        </h1>
        {section === 'account' && (
          <button
            onClick={handleSaveAccount}
            disabled={isSaving}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${isSaving || saved ? 'bg-[#A38BFF]/30 text-[#A38BFF]/50' : 'bg-[#A38BFF] text-[#0B0D10] active:scale-95'}`}
          >
            {isSaving ? <Loader2 size={14} className="animate-spin" /> : saved ? <Check size={14} /> : 'Save'}
          </button>
        )}
      </div>

      <div ref={contentRef} className="px-4 py-4 space-y-4">
        {/* ── Main menu ── */}
        {section === 'main' && (
          <>
            {/* Profile summary */}
            <div className="flex items-center gap-3 p-4 bg-[#141821] rounded-2xl mb-2">
              {(userRecord?.avatarUrl as string) || (userRecord?.avatar as string) ? (
                <img src={processAvatarUrl((userRecord?.avatarUrl || userRecord?.avatar) as string) || ''} alt="Avatar" className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#A38BFF]/20 flex items-center justify-center">
                  <User size={20} className="text-[#A38BFF]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#F5F7FF] truncate">
                  {(userRecord?.displayName as string) || (userRecord?.name as string) || user?.username || 'You'}
                </p>
                {user?.username && <p className="text-xs text-[#A7B0C8]">@{user.username}</p>}
              </div>
            </div>

            <SectionCard>
              <NavRow icon={<User size={20} />} label="Account" description="Name, email, password" onClick={() => setSection('account')} />
              <NavRow icon={<Bell size={20} />} label="Notifications" description="Push, email preferences" onClick={() => setSection('notifications')} />
              <NavRow icon={<Lock size={20} />} label="Privacy" description="Account visibility, activity" onClick={() => setSection('privacy')} />
              <NavRow icon={<Palette size={20} />} label="Appearance" description="Theme, display options" onClick={() => setSection('appearance')} />
            </SectionCard>

            <SectionCard>
              <NavRow icon={<Shield size={20} />} label="Blocked Users" description="Manage blocked accounts" onClick={() => {}} />
              <NavRow icon={<BookOpen size={20} />} label="Articles" description="Browse blog posts and guides" onClick={() => navigate('/blog')} />
            </SectionCard>

            <SectionCard>
              <NavRow icon={<LogOut size={20} />} label="Log Out" onClick={handleLogout} danger />
              <NavRow icon={<Trash2 size={20} />} label="Delete Account" description="Permanently remove your account" onClick={() => setShowDeleteConfirm(true)} danger />
            </SectionCard>

            <p className="text-xs text-[#A7B0C8] text-center pt-2">Pulse · v1.0</p>
          </>
        )}

        {/* ── Account ── */}
        {section === 'account' && (
          <div className="space-y-4">
            <SectionCard>
              <div className="py-3.5 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-[#A7B0C8] mb-1.5">Display name</label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    maxLength={50}
                    className="w-full h-10 bg-[#0B0D10] rounded-xl px-3 text-sm text-[#F5F7FF] outline-none focus:ring-1 focus:ring-[#A38BFF]/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#A7B0C8] mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    disabled
                    className="w-full h-10 bg-[#0B0D10] rounded-xl px-3 text-sm text-[#A7B0C8] outline-none opacity-60 cursor-not-allowed"
                  />
                </div>
              </div>
            </SectionCard>

            {/* Change password */}
            <div>
              <p className="text-xs font-semibold text-[#A7B0C8] uppercase tracking-wider mb-2 px-1">Change Password</p>
              <SectionCard>
                <div className="py-3.5 space-y-3">
                  <div className="relative">
                    <label className="block text-xs font-medium text-[#A7B0C8] mb-1.5">Current password</label>
                    <input
                      type={showCurrentPw ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full h-10 bg-[#0B0D10] rounded-xl px-3 pr-10 text-sm text-[#F5F7FF] outline-none focus:ring-1 focus:ring-[#A38BFF]/50"
                    />
                    <button
                      aria-label={showCurrentPw ? 'Hide password' : 'Show password'}
                      onClick={() => setShowCurrentPw(v => !v)}
                      className="absolute right-3 bottom-2.5 text-[#A7B0C8]"
                    >
                      {showCurrentPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <div className="relative">
                    <label className="block text-xs font-medium text-[#A7B0C8] mb-1.5">New password</label>
                    <input
                      type={showNewPw ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full h-10 bg-[#0B0D10] rounded-xl px-3 pr-10 text-sm text-[#F5F7FF] outline-none focus:ring-1 focus:ring-[#A38BFF]/50"
                    />
                    <button
                      aria-label={showNewPw ? 'Hide password' : 'Show password'}
                      onClick={() => setShowNewPw(v => !v)}
                      className="absolute right-3 bottom-2.5 text-[#A7B0C8]"
                    >
                      {showNewPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#A7B0C8] mb-1.5">Confirm new password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full h-10 bg-[#0B0D10] rounded-xl px-3 text-sm text-[#F5F7FF] outline-none focus:ring-1 focus:ring-[#A38BFF]/50"
                    />
                  </div>
                  {passwordError && <p className="text-xs text-[#FF4D4D]">{passwordError}</p>}
                  <button
                    onClick={handleChangePassword}
                    disabled={!currentPassword || !newPassword || !confirmPassword}
                    className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                      currentPassword && newPassword && confirmPassword
                        ? 'bg-[#A38BFF] text-[#0B0D10]'
                        : 'bg-[#A38BFF]/20 text-[#A38BFF]/40 cursor-not-allowed'
                    }`}
                  >
                    {passwordSaved ? '✓ Password updated' : 'Update Password'}
                  </button>
                </div>
              </SectionCard>
            </div>
          </div>
        )}

        {/* ── Notifications ── */}
        {section === 'notifications' && (
          <SectionCard>
            <ToggleRow label="Likes" description="When someone likes your post" value={notifLikes} onChange={setNotifLikes} />
            <ToggleRow label="Comments" description="When someone comments on your post" value={notifComments} onChange={setNotifComments} />
            <ToggleRow label="New followers" description="When someone follows you" value={notifFollows} onChange={setNotifFollows} />
            <ToggleRow label="Mentions" description="When someone mentions you" value={notifMentions} onChange={setNotifMentions} />
            <ToggleRow label="Messages" description="When you receive a new message" value={notifMessages} onChange={setNotifMessages} />
          </SectionCard>
        )}

        {/* ── Privacy ── */}
        {section === 'privacy' && (
          <div className="space-y-4">
            <SectionCard>
              <ToggleRow label="Private account" description="Only approved followers can see your posts" value={privateAccount} onChange={setPrivateAccount} />
              <ToggleRow label="Show activity status" description="Let others see when you were last active" value={showActivity} onChange={setShowActivity} />
              <ToggleRow label="Allow mentions" description="Let anyone mention you in posts" value={allowMentions} onChange={setAllowMentions} />
            </SectionCard>
          </div>
        )}

        {/* ── Appearance ── */}
        {section === 'appearance' && (
          <div className="space-y-4">
            <p className="text-xs font-semibold text-[#A7B0C8] uppercase tracking-wider px-1">Theme</p>
            <div className="flex gap-3">
              {(['dark', 'light'] as const).map((t) => (
                <button
                  key={t}
                  disabled={t === 'light'}
                  className={`flex-1 flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-colors ${
                    theme === t
                      ? 'border-[#A38BFF] bg-[#A38BFF]/10'
                      : 'border-white/[0.06] bg-[#141821]'
                  } ${t === 'light' ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  {t === 'dark' ? <Moon size={24} className="text-[#A38BFF]" /> : <Sun size={24} className="text-[#FFD700]" />}
                  <span className="text-sm font-medium text-[#F5F7FF] capitalize">{t}</span>
                  {t === 'light' && <span className="text-[10px] text-[#A7B0C8]">Coming soon</span>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delete account confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[90] bg-black/70 flex items-end" onClick={() => setShowDeleteConfirm(false)}>
          <div
            className="w-full bg-[#0F1117] rounded-t-[24px] p-6"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 24px)', animation: 'slideUpModal 0.25s ease-out' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-[#FF4D4D]/10 rounded-full flex items-center justify-center">
                <AlertTriangle size={24} className="text-[#FF4D4D]" />
              </div>
            </div>
            <h3 className="text-lg font-bold text-[#F5F7FF] text-center mb-2">Delete Account?</h3>
            <p className="text-sm text-[#A7B0C8] text-center mb-6">
              This will permanently delete your account and all your content. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-3 bg-[#141821] rounded-xl text-sm font-semibold text-[#F5F7FF] border border-white/[0.08]"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeletingAccount}
                className="flex-1 py-3 bg-[#FF4D4D] rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2"
              >
                {isDeletingAccount ? <Loader2 size={16} className="animate-spin" /> : null}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
